/* eslint-disable */
/**
 * jobUtils.js
 * Centralized utility helpers for Jobs Portal
 */

/**
 * Formats job salary display text
 * @param {number|null} min
 * @param {number|null} max
 * @param {string|null} salaryText
 * @param {boolean} negotiable
 * @returns {string}
 */
export const getFriendlyErrorMsg = (resDataMessage, fallbackMessage) => {
  if (!resDataMessage) return fallbackMessage;
  const msg = String(resDataMessage).trim();
  const lowerMsg = msg.toLowerCase();
  
  // Check for common backend/SQL/Node/PHP error keywords
  const isTechnicalError = 
    lowerMsg.includes('sql') || 
    lowerMsg.includes('syntax') || 
    lowerMsg.includes('exception') || 
    lowerMsg.includes('database') || 
    lowerMsg.includes('typeerror') || 
    lowerMsg.includes('referenceerror') || 
    lowerMsg.includes('query') ||
    lowerMsg.includes('fatal error') ||
    lowerMsg.includes('undefined') ||
    lowerMsg.includes('null');

  // If it's technical, or entirely English without any Thai characters, we might want to use the fallback.
  // Using Thai char check as a good heuristic for "intended for user" vs "internal system error"
  const hasThaiChars = /[\u0E00-\u0E7F]/.test(msg);

  if (isTechnicalError || (!hasThaiChars && msg.length > 50)) {
    return fallbackMessage;
  }
  
  return msg;
};

/**
 * Formats job salary display text
 * @param {number|null} min
 * @param {number|null} max
 * @param {string|null} salaryText
 * @param {boolean} negotiable
 * @returns {string}
 */
export const formatSalary = (min, max, salaryText = '', negotiable = false) => {
  if (salaryText && salaryText.trim() !== '') {
    return salaryText;
  }
  if (min && max && min > 0 && max > 0) {
    return `${min.toLocaleString()} - ${max.toLocaleString()} บาท`;
  }
  if (min && min > 0) {
    return `${min.toLocaleString()}+ บาท`;
  }
  if (negotiable) {
    return 'ตามตกลง';
  }
  return 'ตามตกลง';
};

/**
 * Formats ISO or DB datetime to short date string (YYYY-MM-DD or Thai)
 * @param {string|null} dateStr
 * @returns {string}
 */
export const formatShortDate = (dateStr) => {
  if (!dateStr) return 'ล่าสุด';
  try {
    return dateStr.split(' ')[0] || dateStr;
  } catch (e) {
    return 'ล่าสุด';
  }
};

/**
 * Formats date into Thai locale (e.g. 15 ส.ค. 2567)
 * @param {string|null} dateStr
 * @returns {string}
 */
export const formatThaiDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return formatShortDate(dateStr);
    }
    const thMonths = [
      'ม.ค.',
      'ก.พ.',
      'มี.ค.',
      'เม.ย.',
      'พ.ค.',
      'มิ.ย.',
      'ก.ค.',
      'ส.ค.',
      'ก.ย.',
      'ต.ค.',
      'พ.ย.',
      'ธ.ค.',
    ];
    return `${d.getDate()} ${thMonths[d.getMonth()]} ${d.getFullYear() + 543}`;
  } catch (e) {
    return formatShortDate(dateStr);
  }
};

/**
 * Calculates straight line distance in km between two geo-coordinates (Haversine formula)
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} Distance in kilometers
 */
export const toValidCoord = (value) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

export const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const aLat = toValidCoord(lat1);
  const aLon = toValidCoord(lon1);
  const bLat = toValidCoord(lat2);
  const bLon = toValidCoord(lon2);
  if (aLat === null || aLon === null || bLat === null || bLon === null) return 0;
  const R = 6371; // Earth's radius in km
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLon - aLon) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((aLat * Math.PI) / 180) *
      Math.cos((bLat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
};

export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Shared career categories (id aligned across seeker + employer forms) */
export const JOB_CAREER_CATEGORIES = [
  { id: 1, title: 'แพทย์', icon: 'mdi-stethoscope' },
  { id: 2, title: 'พยาบาล', icon: 'mdi-heart-pulse' },
  { id: 3, title: 'เภสัชกร', icon: 'mdi-pill' },
  { id: 4, title: 'เทคนิคการแพทย์', icon: 'mdi-microscope' },
  { id: 5, title: 'ทันตแพทย์', icon: 'mdi-tooth' },
  { id: 6, title: 'นักกายภาพบำบัด', icon: 'mdi-human-handsdown' },
  { id: 7, title: 'รังสีเทคนิค', icon: 'mdi-radioactive' },
  { id: 8, title: 'เจ้าหน้าที่สาธารณสุข', icon: 'mdi-hospital-building' },
];

export const JOB_CATEGORY_NAME_TO_ID = {
  แพทย์: 1,
  พยาบาล: 2,
  พยาบาลวิชาชีพ: 2,
  ผู้ช่วยพยาบาล: 2,
  เภสัชกร: 3,
  ผู้ช่วยเภสัชกร: 3,
  เทคนิคการแพทย์: 4,
  ทันตแพทย์: 5,
  นักกายภาพบำบัด: 6,
  กายภาพบำบัด: 6,
  รังสีเทคนิค: 7,
  เจ้าหน้าที่สาธารณสุข: 8,
  'งานบริการลูกค้า/แอดมิน': 8,
};

/**
 * Suggested professional skills dynamically mapped by Career Category ID
 */
export const SUGGESTED_SKILLS_BY_CATEGORY = {
  1: [
    'ใบอนุญาตประกอบวิชาชีพเวชกรรม',
    'เวชปฏิบัติทั่วไป',
    'การตรวจวินิจฉัยโรคเบื้องต้น',
    'การดูแลผู้ป่วยกลุ่มโรคเรื้อรัง (NCDs)',
    'เวชศาสตร์ฉุกเฉิน / ER',
    'BLS / ACLS การช่วยฟื้นคืนชีพขั้นสูง',
    'การสื่อสารภาษาอังกฤษเพื่อการแพทย์',
  ],
  2: [
    'ใบอนุญาตประกอบวิชาชีพการพยาบาล',
    'การพยาบาลผู้ป่วยใน (IPD)',
    'การพยาบาลผู้ป่วยนอก (OPD)',
    'การให้สารน้ำและยาทางหลอดเลือด (IV)',
    'การทำแผลและหัตถการเบื้องต้น',
    'BLS / CPR การปฐมพยาบาลเบื้องต้น',
    'การบันทึกประวัติทางการพยาบาล',
  ],
  3: [
    'ใบอนุญาตประกอบวิชาชีพเภสัชกรรม',
    'เภสัชกรรมชุมชน',
    'ระบบ POS ร้านยา',
    'การบริบาลทางเภสัชกรรม (Pharmaceutical Care)',
    'การให้คำปรึกษาเรื่องยา (Patient Counseling)',
    'การบริหารคลังยาและเวชภัณฑ์',
    'การดูแลผู้ป่วยกลุ่มโรคเรื้อรัง (NCDs)',
  ],
  4: [
    'ใบอนุญาตประกอบวิชาชีพเทคนิคการแพทย์',
    'การตรวจวิเคราะห์ทางห้องปฏิบัติการ (Lab)',
    'การเจาะเลือดผู้ป่วย (Phlebotomy)',
    'การควบคุมคุณภาพห้องปฏิบัติการ (QC)',
    'เครื่องตรวจวิเคราะห์อัตโนมัติ (Automated Analyzer)',
    'ความปลอดภัยทางชีวภาพ (Biosafety)',
  ],
  5: [
    'ใบอนุญาตประกอบวิชาชีพทันตกรรม',
    'ทันตกรรมทั่วไป (General Dentistry)',
    'การขูดหินปูนและอุดฟัน',
    'การผ่าฟันคุด / ถอนฟัน',
    'การถ่ายและแปลผลภาพรังสีฟัน (Dental X-ray)',
    'การจัดฟันเบื้องต้น',
  ],
  6: [
    'ใบอนุญาตประกอบวิชาชีพกายภาพบำบัด',
    'กายภาพบำบัดระบบกระดูกและกล้ามเนื้อ (Office Syndrome)',
    'การฟื้นฟูสมรรถภาพผู้ป่วยโรคหลอดเลือดสมอง (Stroke)',
    'การใช้เครื่องมือกายภาพบำบัด (Ultrasound / Shockwave)',
    'การออกกำลังกายเพื่อการบำบัด (Therapeutic Exercise)',
    'การบำบัดด้วยการดัดดึงข้อต่อ (Manual Therapy)',
  ],
  7: [
    'ใบอนุญาตประกอบวิชาชีพทางรังสีวิทยา',
    'การถ่ายภาพรังสีทั่วไป (General X-ray)',
    'การตรวจเอกซเรย์คอมพิวเตอร์ (CT Scan)',
    'การตรวจด้วยคลื่นแม่เหล็กไฟฟ้า (MRI)',
    'การดูแลความปลอดภัยจากรังสี (Radiation Protection)',
    'การใช้ระบบ PACS',
  ],
  8: [
    'งานเวชระเบียนและต้อนรับผู้ป่วย',
    'การจัดการคิวและนัดหมายคนไข้',
    'ระบบสารสนเทศโรงพยาบาล / คลินิก (HIS / Clinic Software)',
    'การเบิกจ่ายสิทธิการรักษาพยาบาล (ประกันสังคม/บัตรทอง/ประกันชีวิต)',
    'การสื่อสารและการบริการลูกค้า (Customer Service)',
    'Microsoft Office / Google Workspace',
  ],
};

export const DEFAULT_SUGGESTED_SKILLS = [
  'BLS / CPR การปฐมพยาบาลเบื้องต้น',
  'การสื่อสารภาษาอังกฤษเพื่อการแพทย์',
  'ระบบสารสนเทศทางการแพทย์ (HIS / Clinic Software)',
  'การดูแลผู้ป่วยและการบริการด้วยใจ',
  'Microsoft Office / Google Workspace',
];

/**
 * Sanitizes initial profile response from API, removing default mock/dummy values for new users
 */
export const sanitizeJobProfileResponse = (rawProfile, fallbackName = '') => {
  if (!rawProfile) return null;

  const isDummyProfile =
    !rawProfile.profile_id ||
    rawProfile.profile_id === 0 ||
    rawProfile.headline === 'บุคลากรทางการแพทย์ / เภสัชกร' ||
    rawProfile.full_name === 'ผู้สมัครงาน MEYA';

  const cleanName = isDummyProfile
    ? (fallbackName || (rawProfile.full_name !== 'ผู้สมัครงาน MEYA' ? rawProfile.full_name : '') || '').trim()
    : (rawProfile.full_name || fallbackName || '').trim();

  const cleanHeadline =
    isDummyProfile && rawProfile.headline === 'บุคลากรทางการแพทย์ / เภสัชกร'
      ? ''
      : (rawProfile.headline || '').trim();

  const cleanCategoryId =
    isDummyProfile && (!rawProfile.category_id || rawProfile.category_id === 0)
      ? null
      : (rawProfile.category_id && rawProfile.category_id !== 0 ? Number(rawProfile.category_id) : null);

  return {
    isDummyProfile,
    fullName: cleanName,
    headline: cleanHeadline,
    targetTitle: cleanHeadline,
    categoryId: cleanCategoryId,
  };
};

/**
 * Infer career category from job title / free text.
 * Order matters: more specific phrases first (e.g. ทันตแพทย์ before แพทย์).
 */
export const inferJobCategoryFromText = (text) => {
  const source = String(text || '');
  if (!source.trim()) return '';

  const rules = [
    { needles: ['ทันตแพทย์', 'ทันตาภิบาล', 'ทันตกรรม', 'dentist'], category: 'ทันตแพทย์' },
    { needles: ['รังสีเทคนิค', 'รังสี', 'เอ็กซเรย์', 'x-ray', 'xray'], category: 'รังสีเทคนิค' },
    {
      needles: ['นักกายภาพบำบัด', 'กายภาพบำบัด', 'physiotherapist', 'physio'],
      category: 'นักกายภาพบำบัด',
    },
    {
      needles: ['เทคนิคการแพทย์', 'นักเทคนิค', 'แล็บ', 'ห้องแล็บ', 'lab tech'],
      category: 'เทคนิคการแพทย์',
    },
    {
      needles: ['ผู้ช่วยพยาบาล', 'พยาบาลวิชาชีพ', 'พยาบาลไตเทียม', 'พยาบาล', 'nurse', 'rn'],
      category: 'พยาบาล',
    },
    {
      needles: ['ผู้ช่วยเภสัชกร', 'เภสัชกร', 'เภสัชกรรม', 'pharmacy', 'pharmacist'],
      category: 'เภสัชกร',
    },
    { needles: ['แพทย์แผนไทย', 'แพทย์'], category: 'แพทย์' },
    {
      needles: ['เจ้าหน้าที่สาธารณสุข', 'สาธารณสุข', 'แอดมิน', 'admin', 'ลูกค้าสัมพันธ์'],
      category: 'เจ้าหน้าที่สาธารณสุข',
    },
  ];

  const lower = source.toLowerCase();
  for (const rule of rules) {
    if (rule.needles.some((n) => source.includes(n) || lower.includes(String(n).toLowerCase()))) {
      return rule.category;
    }
  }
  return '';
};

/** Prefer title-based category when API category is missing or wrong (e.g. all saved as เภสัชกร) */
export const resolveJobCategory = (title, apiCategory = '') => {
  const fromTitle = inferJobCategoryFromText(title);
  if (fromTitle) return fromTitle;

  const fromApiName = inferJobCategoryFromText(apiCategory);
  if (fromApiName) return fromApiName;

  if (apiCategory && JOB_CATEGORY_NAME_TO_ID[apiCategory]) return apiCategory;

  // Map known API aliases
  const api = String(apiCategory || '');
  if (api.includes('พยาบาล')) return 'พยาบาล';
  if (api.includes('เภสัช')) return 'เภสัชกร';
  if (api.includes('ทันต')) return 'ทันตแพทย์';
  if (api.includes('แพทย์')) return 'แพทย์';

  return apiCategory || '';
};

export const getShopContext = (permitdata = null) => {
  const fromPermit =
    permitdata?.shop_id && permitdata.shop_id !== '-'
      ? {
          shopId: permitdata.shop_id,
          shopName: permitdata.shop_name || '',
          shopLogo:
            permitdata.shop_logo && permitdata.shop_logo !== '-' ? permitdata.shop_logo : '',
          token: permitdata.token && permitdata.token !== '-' ? permitdata.token : '',
        }
      : null;
  if (fromPermit) return fromPermit;
  try {
    const stored = sessionStorage.getItem('meya-token4');
    if (stored && stored !== 'undefined') {
      const parsed = JSON.parse(stored);
      if (parsed?.shop_id && parsed.shop_id !== '-') {
        return {
          shopId: parsed.shop_id,
          shopName: parsed.shop_name || '',
          shopLogo: parsed.shop_logo && parsed.shop_logo !== '-' ? parsed.shop_logo : '',
          token: parsed.token && parsed.token !== '-' ? parsed.token : '',
        };
      }
    }
  } catch (e) {}
  return { shopId: '', shopName: '', shopLogo: '', token: '' };
};

export const withStableRowIds = (rows, prefix = 'row') => {
  if (!Array.isArray(rows)) return [];
  return rows.map((item, index) => ({
    ...item,
    id: item?.id ?? `${prefix}-${index}-${Date.now()}`,
  }));
};

/**
 * Fetch every page from a jobs-style list API until exhausted.
 * Prevents silent truncation when page_size caps results (e.g. employer > 50 jobs).
 */
export const fetchAllPagedItems = async ({
  request,
  basePayload = {},
  pageSize = 50,
  maxPages = 40,
  getItems = (data) => (Array.isArray(data?.items) ? data.items : []),
  getTotal = (data) => data?.total ?? data?.total_count,
}) => {
  const all = [];
  let page = 1;
  let totalPages = 1;

  do {
    try {
      const res = await request({
        ...basePayload,
        page,
        page_size: pageSize,
      });
      if (!(res?.data && res.data.status === 'Success' && res.data.data)) break;

      const rows = getItems(res.data.data) || [];
      all.push(...rows);

      const total = Number(getTotal(res.data.data) ?? all.length);
      totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);

      if (!rows.length || rows.length < pageSize) break;
      page += 1;
    } catch (err) {
      // Keep pages already loaded if later pages fail (API may not support paging)
      break;
    }
  } while (page <= totalPages && page <= maxPages);

  return all;
};

const CLEARED_IMAGES_STORAGE_KEY = 'meya_job_cleared_images_v1';

const readClearedImageMap = () => {
  try {
    const raw = sessionStorage.getItem(CLEARED_IMAGES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

/** Remember jobs whose posters were cleared locally when API keeps stale poster_image */
export const markJobImagesCleared = (jobId) => {
  if (!jobId) return;
  const map = readClearedImageMap();
  map[String(jobId)] = Date.now();
  try {
    sessionStorage.setItem(CLEARED_IMAGES_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {}
};

export const unmarkJobImagesCleared = (jobId) => {
  if (!jobId) return;
  const map = readClearedImageMap();
  if (!map[String(jobId)]) return;
  delete map[String(jobId)];
  try {
    sessionStorage.setItem(CLEARED_IMAGES_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {}
};

export const isJobImagesCleared = (jobId) => {
  if (!jobId) return false;
  return Boolean(readClearedImageMap()[String(jobId)]);
};

/** Normalize API gallery + poster into display list; respect intentional clears */
export const resolveJobPosterImages = ({
  jobId,
  posterImage,
  galleryImages,
  logoUrl,
  preferLogoWhenEmpty = true,
} = {}) => {
  const logo = logoUrl || '';
  const cleared = isJobImagesCleared(jobId);

  let list = [];
  let galleryExplicitEmpty = false;

  if (Array.isArray(galleryImages)) {
    list = galleryImages.filter(Boolean);
    galleryExplicitEmpty = galleryImages.length === 0;
  } else if (typeof galleryImages === 'string' && galleryImages.trim()) {
    try {
      const parsed = JSON.parse(galleryImages);
      if (Array.isArray(parsed)) {
        list = parsed.filter(Boolean);
        galleryExplicitEmpty = parsed.length === 0;
      } else if (galleryImages.startsWith('http') || galleryImages.startsWith('/')) {
        list = [galleryImages];
      }
    } catch (e) {
      if (galleryImages.startsWith('http') || galleryImages.startsWith('/')) {
        list = [galleryImages];
      }
    }
  }

  // If user cleared images (or API returned empty gallery), do NOT revive stale poster_image
  if (cleared || galleryExplicitEmpty) {
    return {
      images: preferLogoWhenEmpty && logo ? [logo] : [],
      hasCustomPoster: false,
      cover: preferLogoWhenEmpty ? logo : '',
    };
  }

  if (list.length === 0 && posterImage) {
    list = [posterImage];
  }

  if (list.length === 0 && preferLogoWhenEmpty && logo) {
    return { images: [logo], hasCustomPoster: false, cover: logo };
  }

  return {
    images: list,
    hasCustomPoster: list.length > 0,
    cover: list[0] || logo || '',
  };
};

/**
 * Retrieves current active job user identity from session/local storage or permitdata
 * Centralizes storage access to prevent scattered and out-of-sync storage calls
 * @param {object|null} permitdata
 * @returns {{ userId: string, phone: string, token: string, isLoggedIn: boolean, role: string }}
 */
export const getJobCurrentUser = (permitdata = null) => {
  const userId =
    sessionStorage.getItem('job_user_id') ||
    localStorage.getItem('job_user_id') ||
    permitdata?.user_id ||
    permitdata?.shop_id ||
    '';

  const phone =
    sessionStorage.getItem('job_user_phone') ||
    localStorage.getItem('job_user_phone') ||
    permitdata?.phone ||
    '';

  const token = sessionStorage.getItem('job_token') || localStorage.getItem('job_token') || '';

  const isLoggedIn = Boolean(
    userId ||
      sessionStorage.getItem('meya-job-loggedin') === 'true' ||
      permitdata?.shop_id ||
      permitdata?.user_id,
  );

  const role = permitdata?.shop_id ? 'employer' : 'job_seeker';

  return { userId, phone, token, isLoggedIn, role };
};

/**
 * Clears all job-related session and storage entries cleanly
 */
export const clearJobSession = () => {
  sessionStorage.removeItem('job_token');
  sessionStorage.removeItem('job_user_id');
  sessionStorage.removeItem('job_user_phone');
  sessionStorage.removeItem('job_user_email');
  sessionStorage.removeItem('meya-job-loggedin');
  sessionStorage.removeItem('meya-job-username');
  localStorage.removeItem('job_token');
  localStorage.removeItem('job_user_id');
  localStorage.removeItem('job_user_phone');
};

/** Approximate province centroids (used when API lat/lng missing or wrongly defaulted to Bangkok) */
export const THAI_PROVINCE_COORDS = {
  กระบี่: [8.0863, 98.9063],
  กรุงเทพมหานคร: [13.7563, 100.5018],
  กาญจนบุรี: [14.0227, 99.5328],
  กาฬสินธุ์: [16.4322, 103.5061],
  กำแพงเพชร: [16.4827, 99.5226],
  ขอนแก่น: [16.4419, 102.836],
  จันทบุรี: [12.6113, 102.1038],
  ฉะเชิงเทรา: [13.6904, 101.0779],
  ชลบุรี: [13.3611, 100.9847],
  ชัยนาท: [15.185, 100.125],
  ชัยภูมิ: [15.8067, 102.0315],
  ชุมพร: [10.493, 99.18],
  เชียงราย: [19.9105, 99.8406],
  เชียงใหม่: [18.7883, 98.9853],
  ตรัง: [7.5594, 99.611],
  ตราด: [12.2428, 102.5177],
  ตาก: [16.884, 99.125],
  นครนายก: [14.2069, 101.213],
  นครปฐม: [13.8196, 100.0621],
  นครพนม: [17.392, 104.77],
  นครราชสีมา: [14.9799, 102.0977],
  นครศรีธรรมราช: [8.4304, 99.9631],
  นครสวรรค์: [15.7047, 100.1372],
  นนทบุรี: [13.8621, 100.5144],
  นราธิวาส: [6.4254, 101.8253],
  น่าน: [18.7756, 100.773],
  บึงกาฬ: [18.3609, 103.6466],
  บุรีรัมย์: [14.993, 103.1029],
  ปทุมธานี: [14.0208, 100.525],
  ประจวบคีรีขันธ์: [11.812, 99.797],
  ปราจีนบุรี: [14.0504, 101.372],
  ปัตตานี: [6.868, 101.25],
  พระนครศรีอยุธยา: [14.3532, 100.56896],
  พะเยา: [19.166, 99.901],
  พังงา: [8.4502, 98.525],
  พัทลุง: [7.6167, 100.083],
  พิจิตร: [16.443, 100.347],
  พิษณุโลก: [16.8211, 100.2659],
  เพชรบุรี: [13.111, 99.944],
  เพชรบูรณ์: [16.419, 101.16],
  แพร่: [18.1445, 100.1403],
  ภูเก็ต: [7.8804, 98.3923],
  มหาสารคาม: [16.185, 103.3],
  มุกดาหาร: [16.545, 104.72],
  แม่ฮ่องสอน: [19.302, 97.965],
  ยโสธร: [15.794, 104.145],
  ยะลา: [6.541, 101.28],
  ร้อยเอ็ด: [16.053, 103.653],
  ระนอง: [9.9659, 98.6348],
  ระยอง: [12.6833, 101.237],
  ราชบุรี: [13.5283, 99.8134],
  ลพบุรี: [14.7995, 100.6534],
  ลำปาง: [18.2888, 99.4909],
  ลำพูน: [18.574, 99.0087],
  เลย: [17.486, 101.722],
  ศรีสะเกษ: [15.1186, 104.322],
  สกลนคร: [17.154, 104.148],
  สงขลา: [7.189, 100.595],
  สตูล: [6.6238, 100.067],
  สมุทรปราการ: [13.599, 100.5998],
  สมุทรสงคราม: [13.4098, 100.002],
  สมุทรสาคร: [13.5475, 100.274],
  สระแก้ว: [13.824, 102.0645],
  สระบุรี: [14.5289, 100.9101],
  สิงห์บุรี: [14.887, 100.401],
  สุโขทัย: [17.0056, 99.826],
  สุพรรณบุรี: [14.4744, 100.1177],
  สุราษฎร์ธานี: [9.1382, 99.3331],
  สุรินทร์: [14.882, 103.4937],
  หนองคาย: [17.8782, 102.742],
  หนองบัวลำภู: [17.2218, 102.426],
  อ่างทอง: [14.5896, 100.455],
  อำนาจเจริญ: [15.86, 104.626],
  อุดรธานี: [17.4156, 102.787],
  อุตรดิตถ์: [17.62, 100.0993],
  อุทัยธานี: [15.381, 100.026],
  อุบลราชธานี: [15.244, 104.847],
};

const BANGKOK_FALLBACK_LAT = 13.7563;
const BANGKOK_FALLBACK_LNG = 100.5018;

export const isDefaultBangkokCoord = (lat, lng) => {
  const a = toValidCoord(lat);
  const b = toValidCoord(lng);
  if (a === null || b === null) return false;
  return (
    Math.abs(a - BANGKOK_FALLBACK_LAT) < 0.00015 && Math.abs(b - BANGKOK_FALLBACK_LNG) < 0.00015
  );
};

export const detectProvinceFromText = (text) => {
  const source = String(text || '');
  if (!source.trim()) return '';

  // 1. Check for explicit non-Bangkok province names in text first (longer names first)
  const nonBkkNames = Object.keys(THAI_PROVINCE_COORDS)
    .filter((name) => name !== 'กรุงเทพมหานคร')
    .sort((a, b) => b.length - a.length);

  for (const name of nonBkkNames) {
    if (source.includes(name)) return name;
  }

  // 2. Common district / amphoe hints when province field was wrongly saved as Bangkok
  const districtHints = [
    ['บางพลี', 'สมุทรปราการ'],
    ['บางบ่อ', 'สมุทรปราการ'],
    ['พระประแดง', 'สมุทรปราการ'],
    ['เมืองสมุทรปราการ', 'สมุทรปราการ'],
    ['ปากน้ำ', 'สมุทรปราการ'],
    ['บางเสาธง', 'สมุทรปราการ'],
    ['เมืองสมุทรสาคร', 'สมุทรสาคร'],
    ['กระทุ่มแบน', 'สมุทรสาคร'],
    ['บ้านแพ้ว', 'สมุทรสาคร'],
    ['เมืองสมุทรสงคราม', 'สมุทรสงคราม'],
    ['เมืองนนทบุรี', 'นนทบุรี'],
    ['ปากเกร็ด', 'นนทบุรี'],
    ['บางบัวทอง', 'นนทบุรี'],
    ['เมืองปทุมธานี', 'ปทุมธานี'],
    ['รังสิต', 'ปทุมธานี'],
    ['คลองหลวง', 'ปทุมธานี'],
    ['ลำลูกกา', 'ปทุมธานี'],
    ['ศรีราชา', 'ชลบุรี'],
    ['บางละมุง', 'ชลบุรี'],
    ['พัทยา', 'ชลบุรี'],
    ['เมืองชลบุรี', 'ชลบุรี'],
    ['หาดใหญ่', 'สงขลา'],
    ['เมืองเชียงใหม่', 'เชียงใหม่'],
    ['เมืองขอนแก่น', 'ขอนแก่น'],
    ['เมืองนครราชสีมา', 'นครราชสีมา'],
    ['โคราช', 'นครราชสีมา'],
  ];
  for (const [district, province] of districtHints) {
    if (source.includes(district)) return province;
  }

  // 3. Genuine Bangkok hints
  if (
    source.includes('กรุงเทพมหานคร') ||
    source.includes('กรุงเทพ') ||
    source.includes('กทม') ||
    source.includes('บางนา') ||
    source.includes('ลาดกระบัง') ||
    source.includes('มีนบุรี')
  ) {
    return 'กรุงเทพมหานคร';
  }

  return '';
};

export const getProvinceCoords = (provinceOrText) => {
  const province = THAI_PROVINCE_COORDS[provinceOrText]
    ? provinceOrText
    : detectProvinceFromText(provinceOrText);
  const pair = THAI_PROVINCE_COORDS[province];
  if (!pair) return null;
  return { lat: pair[0], lng: pair[1], province };
};

/** Spread pins that share the same province centroid so they don't stack */
export const spreadCoordsById = (lat, lng, id) => {
  if (lat === null || lng === null) return { lat, lng };
  const seed = String(id || '')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const angle = (seed % 360) * (Math.PI / 180);
  const radius = 0.01 + (seed % 7) * 0.003; // ~1–3 km
  return {
    lat: lat + Math.sin(angle) * radius,
    lng: lng + Math.cos(angle) * radius,
  };
};

/**
 * Resolve display coordinates for a job/candidate item.
 * Prefers real API coords; if missing or stuck on Bangkok default while text says another province,
 * fall back to province centroid.
 */
export const resolveMapCoords = (item = {}) => {
  const lat = toValidCoord(item.lat ?? item.latitude);
  const lng = toValidCoord(item.lng ?? item.longitude ?? item.lon);

  // Check explicit free-text fields first (without item.province)
  // to avoid a defaulted "กรุงเทพมหานคร" overriding the real province mentioned in address/title
  const freeText = [
    item.address,
    item.title,
    item.district,
    item.location,
    item.company,
    item.name,
  ]
    .filter(Boolean)
    .join(' ');
  const textProvinceHint = detectProvinceFromText(freeText);

  // If address/title has an explicit province, prefer it; otherwise fallback to item.province
  const provinceHint =
    textProvinceHint ||
    detectProvinceFromText(item.province) ||
    item.province ||
    '';

  const provinceFallback = getProvinceCoords(provinceHint);

  const hasRealCoords = lat !== null && lng !== null && !(lat === 0 && lng === 0);
  const stuckOnBangkokDefault = hasRealCoords && isDefaultBangkokCoord(lat, lng);

  // Bangkok default coords are treated as untrusted whenever address/title points to another province
  if (
    stuckOnBangkokDefault &&
    textProvinceHint &&
    textProvinceHint !== 'กรุงเทพมหานคร' &&
    provinceFallback
  ) {
    const spread = spreadCoordsById(
      provinceFallback.lat,
      provinceFallback.lng,
      item.id || item.job_id,
    );
    return {
      lat: spread.lat,
      lng: spread.lng,
      source: 'province-fix',
      province: provinceFallback.province,
    };
  }

  // Missing coords → province / district centroid
  if (!hasRealCoords && provinceFallback) {
    const spread = spreadCoordsById(
      provinceFallback.lat,
      provinceFallback.lng,
      item.id || item.job_id,
    );
    return {
      lat: spread.lat,
      lng: spread.lng,
      source: 'province',
      province: provinceFallback.province,
    };
  }

  if (hasRealCoords && !stuckOnBangkokDefault) {
    return {
      lat,
      lng,
      source: 'api',
      province: provinceFallback?.province || item.province || '',
    };
  }

  // Genuine Bangkok (default coords + Bangkok/no other hint)
  if (hasRealCoords && stuckOnBangkokDefault) {
    const spread = spreadCoordsById(lat, lng, item.id || item.job_id);
    return {
      lat: spread.lat,
      lng: spread.lng,
      source: 'bangkok-default',
      province: 'กรุงเทพมหานคร',
    };
  }

  return { lat: null, lng: null, source: 'none', province: '' };
};
